import * as helios from "@hyperionbt/helios";
import cx from "clsx";
import { Artwork } from "kreate-common/modules/business-types";
import { DisplayableError } from "kreate-common/modules/displayable-error";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { parseKreateUtxoArtwork } from "kreate-common/modules/utxo-parser/by-type/artwork";
import { CLIENT_ENV } from "kreate-env/client";
import { buildPartialTx$CreateOffer } from "kreate-protocol/offchain/tx/createOffer";
import { httpGet$GetTxStatus } from "kreate-server/commands/GetTxStatus/fetcher";
import React from "react";
import { MdEdit, MdFavorite, MdInfoOutline } from "react-icons/md";

import InputNumEditions$Compact from "../../../../components/InputNumEditions$Compact";
import LabelledValue from "../../../../components/LabelledValue";
import PanelLayout$Emphasis from "../../../../components/PanelLayout$Emphasis";
import { useProtocolParamsUTxO } from "../../../SectionEnableMinting/hooks/useProtocolParamsUTxO";
import { runFlow$UpdateMintingDetails } from "../MethodOnDemandMinting/generators/runFlow$UpdateMintingDetails";

import styles from "./index.module.scss";

import { useCardanoWalletApi } from "@/modules/cardano-wallet";
import { getAddressFromCip30Wallet } from "@/modules/cardano-wallet/utils";
import { useBusyCount } from "@/modules/common-hooks/useBusyCount";
import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { waitUntil } from "@/modules/common-utils/async";
import {
  formatInteger$Compact,
  formatInteger$Standard,
  formatLovelaceAmount$Compact,
  formatLovelaceAmount$Standard,
} from "@/modules/common-utils/format";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { useField$Natural } from "@/modules/form-field/hooks/useField$Natural";
import ErrorViewer from "@/modules/kreate-components/components/ErrorViewer";
import InlineErrorViewer from "@/modules/kreate-components/components/InlineErrorViewer";
import { useModalTrigger$ModalAsyncGenerator } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import { useModalTrigger$ModalConnectWallet } from "@/modules/kreate-modals/modals/ModalConnectWallet/hooks/useModalTrigger$ModalConnectWallet";
import Alert from "@/modules/kreate-modals/modals/ModalEnableMinting$Prompt/components/Alert";
import { useModalTrigger$ModalEnableMinting$Prompt } from "@/modules/kreate-modals/modals/ModalEnableMinting$Prompt/hooks";
import { useModalTrigger$ModalProgress } from "@/modules/kreate-modals/modals/ModalProgress/hooks/useModalTrigger$ModalProgress";
import { useModalTrigger$ModalSuccess } from "@/modules/kreate-modals/modals/ModalSuccess/hooks";
import { useModalTrigger$ModalTransactionBreakdown } from "@/modules/kreate-modals/modals/ModalTransactionBreakdown/hooks";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  artwork: Artwork | null | undefined;
};

export default function MethodReservationBasedMinting({
  className,
  style,
  artwork,
}: Props) {
  const loginStatus = useLoginStatus();
  const wallet = useCardanoWalletApi();
  const { busy, withDefaultErrorHandler } = useBusyCount();

  const [protocolParamsUTxO, protocolParamsUTxO$Error] =
    useProtocolParamsUTxO();

  const modalTriggers = {
    ModalEnableMinting$Prompt: useModalTrigger$ModalEnableMinting$Prompt(),
    ModalConnectWallet: useModalTrigger$ModalConnectWallet(),
    ModalProgress: useModalTrigger$ModalProgress(),
    ModalSuccess: useModalTrigger$ModalSuccess(),
    ModalTransactionBreakdown: useModalTrigger$ModalTransactionBreakdown(),
    ModalAsyncGenerator: useModalTrigger$ModalAsyncGenerator(),
  };

  const datumArtwork =
    artwork?.utxo?.datum.type === "artwork"
      ? artwork.utxo.datum.value
      : undefined;

  const fieldNumEditions = useField$Natural({
    initialValue: 1,
    min: 1,
    max:
      // Ad-hoc: can only mint one for free editions.
      datumArtwork?.mintFee === 0
        ? 1
        : (datumArtwork?.numEditions || 0) - (datumArtwork?.numMinted || 0),
  });

  const handleClickReserveNow = !protocolParamsUTxO
    ? undefined
    : withDefaultErrorHandler(async () => {
        const cip30Wallet =
          await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();

        const { txId } =
          await modalTriggers.ModalProgress.openModal$AsyncFunction<{
            txId: string;
          }>(async (controller) => {
            controller.update({
              text: "Building transaction...",
              ratio: 0.0,
              ratioUpTo: 0.1,
              shrinkRatio: 0.9375,
            });

            const walletAddress = await getAddressFromCip30Wallet(cip30Wallet);
            const wantedQuantity = fieldNumEditions.validateBeforeSubmitting();

            const utxo = artwork?.utxo;
            if (utxo?.datum.type !== "artwork") {
              throw new DisplayableError({
                title: "Error",
                description: "This artwork is not eligible for reserving.",
                cause: { artworkId: artwork?.artworkId, utxo: artwork?.utxo },
              });
            }

            if (protocolParamsUTxO.datum.type !== "protocol-params") {
              throw new DisplayableError({ title: "Invalid protocol params" });
            }

            const { tx, artworkFee, protocolFee, txFee } =
              await buildPartialTx$CreateOffer({
                treasuryAddress: new helios.Address(
                  protocolParamsUTxO.datum.value.treasuryAddress
                ),
                wallet: cip30Wallet,
                datumOffer: {
                  offererAddress: walletAddress,
                  assetId: `${
                    utxo.otherAssets[0].mintingPolicyHash
                  }${helios.bytesToHex(
                    helios.textToBytes(utxo.datum.value.tokenName)
                  )}`,
                  editionPrice: utxo.datum.value.mintFee,
                  wantedQuantity,
                  isSecondary: false,
                  artistAddress: null,
                  editionRoyalty: 0,
                },
                txMessage: undefined,
              });

            const confirmed =
              await modalTriggers.ModalTransactionBreakdown.openModal(
                [
                  {
                    label: "Artwork Price",
                    lovelaceAmount: artworkFee,
                    tooltip: "Edition Price × No. Editions",
                  },
                  {
                    label: "Platform Fee",
                    lovelaceAmount: protocolFee,
                    tooltip:
                      "Our platform receives ≈2.22 ₳; ≈0.55 ₳ covers the delivery transaction; the remainder ≈1.23 ₳ is bound to the Artwork and will be returned to you.",
                  },
                  {
                    label: "Transaction Fee",
                    lovelaceAmount: txFee,
                    tooltip:
                      "The blockchain network fees to process this transaction",
                  },
                ],
                { artwork }
              );
            if (!confirmed) {
              throw null;
            }

            controller.update({
              text: "Waiting for signature and submission...",
              ratio: 0.1,
              ratioUpTo: 0.2,
              shrinkRatio: 0.9375,
            });

            const signatures = await cip30Wallet.signTx(tx);
            tx.addSignatures(signatures);

            const heliosTxId = await cip30Wallet.submitTx(tx);
            const txId = heliosTxId.hex;

            controller.update({
              text: "Waiting for confirmation...",
              ratio: 0.2,
              ratioUpTo: 1.0,
              shrinkRatio: 0.9375,
            });

            await waitUntil(async () => {
              const status = await httpGet$GetTxStatus(
                `/api/v1/tx-status/${txId}`
              );
              return status.ok;
            });

            await shamelesslyRevalidateEverything();

            return { txId };
          });

        await modalTriggers.ModalSuccess.openModal({
          icon: <MdFavorite />,
          content:
            "Your reservation will be fulfilled shortly. Please check your wallet in a few minutes.",
          buttons: [
            {
              content: "View on cexplorer.io",
              onClick: {
                type: "open",
                href: (() => {
                  switch (CLIENT_ENV.CARDANO_NETWORK) {
                    case "Preview":
                      return `https://preview.cexplorer.io/tx/${txId}`;
                    case "Preprod":
                      return `https://preprod.cexplorer.io/tx/${txId}`;
                    default:
                      return `https://cexplorer.io/tx/${txId}`;
                  }
                })(),
                newTab: true,
              },
              isPrimary: true,
            },
            { content: "Close", onClick: "close" },
          ],
        });
      });

  const handleUpdateMintingDetails = withDefaultErrorHandler(async () => {
    if (!artwork?.utxo) return;
    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
    const parsedUtxo = parseKreateUtxoArtwork(artwork.utxo);

    await modalTriggers.ModalAsyncGenerator.openModal(() =>
      runFlow$UpdateMintingDetails({
        initialMintPrice: parsedUtxo.datumArtwork.mintFee,
        initialNumEditions: parsedUtxo.datumArtwork.numEditions,
        cip30Wallet,
        artwork,
      })
    );
  });

  const canUpdateMinting =
    loginStatus?.loggedIn &&
    (loginStatus.isAgent || artwork?.createdBy === loginStatus.userId) &&
    artwork?.utxo &&
    wallet.type === "ready";

  const isFull =
    datumArtwork?.numMinted != null &&
    datumArtwork?.numMinted === datumArtwork?.numEditions;

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Col gap="8px">
        <PanelLayout$Emphasis.Root>
          <PanelLayout$Emphasis.Main>
            <Flex.Row gap="16px 32px" flexWrap="wrap">
              <LabelledValue
                label="Price"
                value={formatLovelaceAmount$Compact(datumArtwork?.mintFee)}
                title={formatLovelaceAmount$Standard(datumArtwork?.mintFee)}
                slotAfterValue={
                  !canUpdateMinting ? undefined : (
                    <ButtonTransparentV2
                      smart={false}
                      icon={<MdEdit size="16px" />}
                      onClick={handleUpdateMintingDetails}
                      disabled={busy || !handleUpdateMintingDetails}
                    />
                  )
                }
              />
              <LabelledValue
                label="Minted"
                value={`${formatInteger$Compact(
                  datumArtwork?.numMinted
                )}/${formatInteger$Compact(datumArtwork?.numEditions)}`}
                title={[
                  `Minted: ${formatInteger$Standard(
                    datumArtwork?.numMinted
                  )} Editions`,
                  `Total: ${formatInteger$Standard(
                    datumArtwork?.numEditions
                  )} Editions`,
                ].join("\n")}
                slotAfterValue={
                  !canUpdateMinting ? undefined : (
                    <ButtonTransparentV2
                      icon={<MdEdit size="16px" />}
                      onClick={handleUpdateMintingDetails}
                      disabled={busy || !handleUpdateMintingDetails}
                    />
                  )
                }
              />
            </Flex.Row>
          </PanelLayout$Emphasis.Main>
          <PanelLayout$Emphasis.Bottom>
            <Flex.Col gap="8px">
              <Flex.Row gap="4px" justifyContent="stretch">
                <Flex.Cell asChild flex="0 0 auto" minWidth="200px">
                  <InputNumEditions$Compact.UseField$Natural
                    api={fieldNumEditions}
                    disabled={busy || isFull}
                  />
                </Flex.Cell>
                <Flex.Cell asChild flex="1 1 auto">
                  <ButtonPrimary
                    size="lg"
                    content={isFull ? "Sold Out" : "Reserve Now"}
                    disabled={busy || isFull}
                    onClick={handleClickReserveNow}
                  />
                </Flex.Cell>
              </Flex.Row>

              {fieldNumEditions.error ? (
                <Flex.Cell>
                  <InlineErrorViewer content={fieldNumEditions.error.message} />
                </Flex.Cell>
              ) : null}
              {protocolParamsUTxO$Error ? (
                <Flex.Cell>
                  <ErrorViewer error={protocolParamsUTxO$Error} />
                </Flex.Cell>
              ) : null}
            </Flex.Col>
          </PanelLayout$Emphasis.Bottom>
        </PanelLayout$Emphasis.Root>
        <Alert
          icon={<MdInfoOutline />}
          content={
            "Require Collectors to make a reservation transaction to mint, " +
            "ensuring orderly minting for high-demand Works. " +
            "Most reservations are processed within minutes."
          }
        />
      </Flex.Col>
    </div>
  );
}
